import { useEffect, useState, useCallback, MouseEvent } from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import { IAvatarItem } from '~/types/common';
import { apiService } from '~/services/apiService';
import styles from './AvatarLightbox.module.css';

interface IAvatarLightboxProps {
  open: boolean;
  userName: string;
  // Wstępne dane (np. pojedynczy URL z wiadomości) — pokazane natychmiast podczas ładowania pełnej galerii.
  initialAvatarUrl?: string | null;
  onClose: () => void;
}

interface IAvatarsResponse {
  nick: string;
  gender: string;
  avatars: IAvatarItem[];
  avatarIndex: number | null;
  avatarUrl: string | null;
}

function AvatarLightbox({ open, userName, initialAvatarUrl, onClose }: IAvatarLightboxProps) {
  const [avatars, setAvatars] = useState<IAvatarItem[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset stanu przy zamknięciu
  useEffect(() => {
    if (!open) {
      setAvatars([]);
      setActiveIndex(0);
      setError(null);
    }
  }, [open]);

  // Pobierz pełną listę zdjęć po otwarciu
  useEffect(() => {
    if (!open || !userName) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiService(`/profile/by-nick/${encodeURIComponent(userName)}/avatars`)
      .then((data: IAvatarsResponse) => {
        if (cancelled) return;
        const list = Array.isArray(data?.avatars) ? data.avatars : [];
        setAvatars(list);
        const idx = typeof data?.avatarIndex === 'number' ? data.avatarIndex : 0;
        setActiveIndex(idx >= 0 && idx < list.length ? idx : 0);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.message || 'Nie udało się pobrać zdjęć.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, userName]);

  const goPrev = useCallback(() => {
    setActiveIndex(i => (i > 0 ? i - 1 : i));
  }, []);

  const goNext = useCallback(() => {
    setActiveIndex(i => (i < avatars.length - 1 ? i + 1 : i));
  }, [avatars.length]);

  // Klawisze: ESC, strzałki
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'ArrowRight') goNext();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose, goPrev, goNext]);

  if (!open) return null;

  const onOverlayClick = (e: MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  // Co pokazać: jeśli mamy listę → wybrany, w przeciwnym razie initialAvatarUrl
  const currentUrl =
    avatars.length > 0 ? avatars[activeIndex]?.url : (initialAvatarUrl || null);

  return ReactDOM.createPortal(
    <div className={styles.overlay} onClick={onOverlayClick}>
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.title}>{userName}</div>
          <button className={styles.closeBtn} onClick={onClose} aria-label="Zamknij">✕</button>
        </div>

        <div className={styles.stage}>
          {loading && avatars.length === 0 && !currentUrl ? (
            <div className={styles.loading}>Ładowanie zdjęć…</div>
          ) : currentUrl ? (
            <img className={styles.image} src={currentUrl} alt={userName} draggable={false} />
          ) : (
            <div className={styles.empty}>
              {error || 'Ten użytkownik nie ma jeszcze zdjęć.'}
            </div>
          )}

          {avatars.length > 1 && (
            <>
              <button
                className={classNames(styles.navBtn, styles.navPrev)}
                onClick={goPrev}
                disabled={activeIndex === 0}
                aria-label="Poprzednie"
              >
                ‹
              </button>
              <button
                className={classNames(styles.navBtn, styles.navNext)}
                onClick={goNext}
                disabled={activeIndex >= avatars.length - 1}
                aria-label="Następne"
              >
                ›
              </button>
            </>
          )}
        </div>

        {avatars.length > 1 && (
          <div className={styles.dots}>
            {avatars.map((a, i) => (
              <button
                key={a.id}
                className={classNames(styles.dot, { [styles.dotActive]: i === activeIndex })}
                onClick={() => setActiveIndex(i)}
                aria-label={`Zdjęcie ${i + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

export default AvatarLightbox;
